import type { PackagingItem } from '@/shared/hooks/api/packagingSuppliers'

export const PACKAGING_ITEM_DECLARATION_OF_CONFORMITY_REQUIREMENT_KEYS = [
  'manufacturerIdentity',
  'declarationObject',
  'heavyMetalsEvidence',
  'substancesOfConcernEvidence',
  'itemExportable',
] as const

export type PackagingItemDeclarationOfConformityRequirementKey =
  (typeof PACKAGING_ITEM_DECLARATION_OF_CONFORMITY_REQUIREMENT_KEYS)[number]

export type PackagingItemDeclarationOfConformityRequirement = {
  key: PackagingItemDeclarationOfConformityRequirementKey
  met: boolean
}

type DocumentRow = {
  documentType: string
  filePathInBucket?: string
}

function getRequiredHmSocDocumentTypes(item: {
  heavyMetalsStatus?: string | null
  substancesOfConcernStatus?: string | null
}): string[] {
  const required: string[] = []
  if (item.heavyMetalsStatus === 'NONE') {
    required.push('HEAVY_METALS_CONFORMITY_STATEMENT')
  } else if (item.heavyMetalsStatus === 'SUFFICIENTLY_MINIMIZED') {
    required.push('HEAVY_METALS_TEST_REPORT')
  }
  if (item.substancesOfConcernStatus === 'NONE') {
    required.push('SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT')
  } else if (item.substancesOfConcernStatus === 'SUFFICIENTLY_MINIMIZED') {
    required.push('SUBSTANCES_OF_CONCERN_TEST_REPORT')
  }
  return required
}

function hasUploadedDocuments(
  documents: DocumentRow[],
  requiredTypes: string[]
): boolean {
  const completedDocuments = documents.filter((document) => {
    if (document.filePathInBucket === undefined) {
      return true
    }
    return document.filePathInBucket.trim().length > 0
  })
  const presentTypes = new Set(
    completedDocuments.map((document) => document.documentType)
  )
  return requiredTypes.every((type) => presentTypes.has(type))
}

function hasComplianceLevelEvidence(
  status: string | null | undefined,
  documentKind: 'heavyMetals' | 'substancesOfConcern',
  documents: DocumentRow[]
): boolean {
  if (!status || status === 'NOT_SUFFICIENTLY_MINIMIZED') {
    return false
  }
  if (status !== 'NONE' && status !== 'SUFFICIENTLY_MINIMIZED') {
    return false
  }

  const requiredTypes = getRequiredHmSocDocumentTypes(
    documentKind === 'heavyMetals'
      ? { heavyMetalsStatus: status }
      : { substancesOfConcernStatus: status }
  )
  return hasUploadedDocuments(documents, requiredTypes)
}

function isItemExportable(item: PackagingItem): boolean {
  if (item.deletedAt != null) {
    return false
  }
  if (item.mergedIntoPackagingItem != null) {
    return false
  }
  return true
}

export function evaluatePackagingItemDeclarationOfConformityRequirements(
  item: PackagingItem
): {
  requirements: PackagingItemDeclarationOfConformityRequirement[]
  allMet: boolean
  exportReady: boolean
  unmetKeys: PackagingItemDeclarationOfConformityRequirementKey[]
  unmetKeysExceptManufacturer: PackagingItemDeclarationOfConformityRequirementKey[]
} {
  const documents = item.documents ?? []

  const manufacturerMet =
    Boolean(item.manufacturerName?.trim()) &&
    Boolean(item.manufacturerAddress?.trim())
  const declarationObjectMet =
    Boolean(item.name?.trim()) && Boolean(item.description?.trim())
  const heavyMetalsMet = hasComplianceLevelEvidence(
    item.heavyMetalsStatus,
    'heavyMetals',
    documents
  )
  const substancesOfConcernMet = hasComplianceLevelEvidence(
    item.substancesOfConcernStatus,
    'substancesOfConcern',
    documents
  )
  const itemExportableMet = isItemExportable(item)

  const requirements: PackagingItemDeclarationOfConformityRequirement[] = [
    { key: 'manufacturerIdentity', met: manufacturerMet },
    { key: 'declarationObject', met: declarationObjectMet },
    { key: 'heavyMetalsEvidence', met: heavyMetalsMet },
    { key: 'substancesOfConcernEvidence', met: substancesOfConcernMet },
    { key: 'itemExportable', met: itemExportableMet },
  ]

  const unmetKeys = requirements
    .filter((requirement) => !requirement.met)
    .map((requirement) => requirement.key)
  const unmetKeysExceptManufacturer = unmetKeys.filter(
    (key) => key !== 'manufacturerIdentity'
  )

  return {
    requirements,
    allMet: unmetKeys.length === 0,
    exportReady: unmetKeysExceptManufacturer.length === 0,
    unmetKeys,
    unmetKeysExceptManufacturer,
  }
}
